import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { UserModel } from '../../../shared/common/viewmodels/user.model';
import { AdminService } from '../../services/admin.service';
import { canDeleteUserViewModel } from '../../../shared/common/viewmodels/canDeleteUserViewModel';
import { UserDeletedModel } from '../../../shared/common/user-deleted.model';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss'],
})
export class UsersListComponent implements OnInit {
  users: Array<UserModel> = [];
  // The name column is sorted alphabetically by first name by default (see
  // setUsersList()) - reflect that as the initially-selected/ascending
  // column rather than leaving the header unhighlighted until clicked.
  usersFilter = 'firstName';
  // Keyed by the same column name as usersFilter, so the template can look
  // up "is the currently-sorted column ascending or descending" generically
  // (isColumnDescending()) instead of needing a separate ngClass condition
  // hardcoded per column.
  sortAscending: { [column: string]: boolean } = {
    firstName: true,
    email: true,
    role: true,
  };
  showLabel = false;
  showLabelCase = '';
  // deletedUser: UserModel;
  deletedUser: UserDeletedModel;
  canDeleteUser: canDeleteUserViewModel;
  deleteFailedReason = '';
  public opened = false;
  public actionsLayout = 'normal';
  userToDeleteId = 0;

  constructor(
    private adminService: AdminService,
    private route: ActivatedRoute
  ) {
    this.route.queryParams.subscribe((params) => {
      if (params.action !== undefined) {
        this.showLabelCase = params.action;
        this.showLabel = true;
        setTimeout(() => {
          this.showLabel = false;
        }, 2500);
      }
    });
  }

  ngOnInit() {
    this.setUsersList();
  }

  setUsersList() {
    this.adminService.getResponseMasterUsers().subscribe((result) => {
      if (result) {
        // The API doesn't guarantee an order, so without this the list
        // jumbles differently on every load - default to the same
        // alphabetical-by-first-name order the name column sorts by.
        this.users = this.sortByFirstNameAscending(result);
      }
    });
  }

  sortUsers(sortName) {
    this.usersFilter = sortName;

    if (sortName === 'firstName') {
      this.sortByFirstName();
    } else if (sortName === 'email') {
      this.sortByEmailAddress();
    } else if (sortName === 'role') {
      this.sortByRole();
    }
  }

  isColumnDescending(column: string): boolean {
    return this.usersFilter === column && !this.sortAscending[column];
  }

  sortByFirstNameAscending(users: Array<UserModel>) {
    return users.sort((a, b) => {
      const aName = (a.firstName || '').toLowerCase();
      const bName = (b.firstName || '').toLowerCase();
      if (aName < bName) {
        return -1;
      }
      if (aName > bName) {
        return 1;
      }
      return 0;
    });
  }

  sortByFirstName() {
    // Toggle first so sortAscending.firstName always reflects the order the
    // click is ABOUT to produce, not the order before it - isColumnDescending()
    // reads it straight after this runs to decide which way the header
    // arrow should point.
    this.sortAscending.firstName = !this.sortAscending.firstName;
    this.users = this.sortByFirstNameAscending(this.users);
    if (!this.sortAscending.firstName) {
      this.users.reverse();
    }
  }

  sortByEmailAddress() {
    this.sortAscending.email = !this.sortAscending.email;
    this.users = this.users.sort((a, b) => {
      if (a.emailAddress.toLowerCase() < b.emailAddress.toLowerCase()) {
        return -1;
      }
      if (a.emailAddress.toLowerCase() > b.emailAddress.toLowerCase()) {
        return 1;
      }
      return 0;
    });
    if (!this.sortAscending.email) {
      this.users.reverse();
    }
  }

  sortByRole() {
    this.sortAscending.role = !this.sortAscending.role;
    this.users = this.users.sort((a, b) => {
      const aRole = (a.role?.name || '').toLowerCase();
      const bRole = (b.role?.name || '').toLowerCase();
      if (aRole < bRole) {
        return -1;
      }
      if (aRole > bRole) {
        return 1;
      }
      return 0;
    });
    if (!this.sortAscending.role) {
      this.users.reverse();
    }
  }

  deleteUser(userId) {
    if (userId === 1) {
      console.log('Admin delete not allowed');
      return;
    }

    this.adminService.canDeleteUser(userId).subscribe((result) => {
      if (!result.canDelete) {
        this.showLabel = true;
        this.showLabelCase = 'deletefailed';
        this.deleteFailedReason = result.reason;
        return;
      } else {
        this.adminService.delete(userId).subscribe((res) => {
          if (res.deleteSuccessful) {
            this.users.forEach((user, index) => {
              if (user.id === userId) {
                this.users.splice(index, 1);
              }
            });
            this.deletedUser = res;
            this.showLabel = true;
            this.showLabelCase = 'delete';
            setTimeout(() => {
              this.showLabel = false;
            }, 2500);
          } else {
            this.showLabel = true;
            this.showLabelCase = 'deletefailed';
            this.deleteFailedReason = res.deleteMessage;
          }
        });
      }
    });
  }

  public close() {
    this.opened = false;
  }

  public open(userId) {
    this.userToDeleteId = userId;
    this.opened = true;
  }

  confirmDelete() {
    this.deleteUser(this.userToDeleteId);
    this.close();
  }
}
